import { CatalogCountry, CatalogOccasion } from '../../services/occasion-catalog'
import { toFaDigits } from '../../helpers/digits'

// What a country calls the things below it, singular and plural. Most of the
// catalog's are states, and the picker and this file both name them out loud.
// Britain's three are nations and France's one is an area, so ایالت is simply
// the wrong word there — and the catalog cannot answer this, because it marks
// England and Wales, Scotland and Northern Ireland as `state` alongside the
// American and German ones.
const REGION_WORDS: Record<string, [string, string]> = {
    GB: ['منطقه', 'مناطق'],
    FR: ['منطقه', 'مناطق'],
    CA: ['استان', 'استان‌ها'],
    ES: ['منطقه', 'مناطق'],
    CH: ['کانتون', 'کانتون‌ها'],
}

export const regionWords = (country: CatalogCountry): [string, string] =>
    REGION_WORDS[country.code] ?? ['ایالت', 'ایالت‌ها']

// Where an occasion applies, said the shortest true way, from where the reader
// is standing.
//
// An occasion with no regions is country-wide. One that covers every region the
// catalog carries is said the same way, and one that misses only a couple is
// written as country-wide with the exceptions named — eleven region names in a
// row is not something anyone reads.
//
// Anything else names the regions the reader picked and counts the rest. Good
// Friday is a holiday in ten US states: someone who picked Delaware is reading
// their own list, and the other nine are only the reason the same day is
// somebody else's holiday too.
//
// "Every region the catalog carries" is the honest limit of this. Germany's
// entry covers four states of sixteen, so what this describes is the data and
// not the country. The country's own note under the list says exactly that,
// which is where the caveat belongs rather than on every row.
export function regionText(
    occasion: CatalogOccasion,
    country: CatalogCountry,
    picked: string[] | undefined,
): string {
    const regions = occasion.regions
    if (!regions?.length) return 'سراسری'

    const nameOf = (code: string) => {
        const found = country.regions.find(r => r.code === code)
        return found?.names.fa ?? found?.names.en ?? code
    }

    // A city is not a state, so Augsburg never counts towards "everywhere".
    const states = country.regions.filter(r => r.level === 'state')
    const missing = states.filter(s => !regions.includes(s.code))

    if (states.length && !missing.length) return 'سراسری'
    // Only worth inverting while the exceptions are genuinely the short half.
    if (states.length && missing.length <= 2 && missing.length * 2 < states.length) {
        return `سراسری به‌جز ${missing.map(s => s.names.fa ?? s.names.en).join('، ')}`
    }

    // Picked regions are top-level, so a nested one — Suffolk County under
    // Massachusetts — matches nothing and falls back to naming itself.
    const mine = picked === undefined ? regions : regions.filter(r => picked.includes(r))
    const named = (mine.length ? mine : regions).slice(0, 3)
    const rest = regions.length - named.length
    const text = named.map(nameOf).join('، ')
    return rest ? `${text} و ${toFaDigits(rest)} ${regionWords(country)[0]} دیگر` : text
}
